/*
  Customer reviews carousel.
  Fetches published Okendo reviews when the section nears the viewport,
  paints the card template, then asks the shared carousel to mount.
*/
(function () {
  const TAG = 'customer-reviews-feed';

  if (customElements.get(TAG)) return;

  const ROOT_MARGIN = '600px 0px';
  const API_ORIGIN = 'https://api.okendo.io/v1';
  const FILTERED_PAGE_SIZE = 25;
  const MAX_PAGES = 3;

  const minimumRating = (value) => {
    const rating = parseInt(value, 10);

    if (rating !== 3 && rating !== 4 && rating !== 5) return 0;

    return rating;
  };

  const meetsRating = (review, minimum) => {
    if (!minimum) return true;

    const rating = Number(review && review.rating);

    return Number.isFinite(rating) && rating >= minimum;
  };

  const nextReviewsUrl = (nextUrl, subscriberId) => {
    if (typeof nextUrl !== 'string' || !subscriberId) return '';

    const storeRoot = API_ORIGIN + '/stores/';

    if (nextUrl.startsWith(storeRoot)) return nextUrl;
    if (nextUrl.startsWith('/stores/')) return API_ORIGIN + nextUrl;

    // The list sometimes returns a path relative to the store, not /stores/{id}.
    if (nextUrl.startsWith('/reviews')) return storeRoot + encodeURIComponent(subscriberId) + nextUrl;

    return '';
  };

  const formatRating = (value) => {
    const rating = Number(value);

    if (!Number.isFinite(rating)) return '';

    const clamped = Math.min(5, Math.max(0, rating));
    const rounded = Math.round(clamped * 10) / 10;

    return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
  };

  const NAMED_ENTITIES = {
    amp: '&',
    lt: '<',
    gt: '>',
    quot: '"',
    apos: "'",
    nbsp: '\u00a0',
    hellip: '\u2026',
    mdash: '\u2014',
    ndash: '\u2013',
    lsquo: '\u2018',
    rsquo: '\u2019',
    ldquo: '\u201c',
    rdquo: '\u201d'
  };

  const decodeText = (value) => value.replace(/&(#x[0-9a-f]+|#\d+|[a-z]+);/gi, (entity, body) => {
    if (body[0] !== '#') {
      const named = NAMED_ENTITIES[body.toLowerCase()];

      return named === undefined ? entity : named;
    }

    const codePoint = body[1] === 'x' || body[1] === 'X'
      ? parseInt(body.slice(2), 16)
      : parseInt(body.slice(1), 10);
    const isSurrogate = codePoint >= 0xd800 && codePoint <= 0xdfff;

    if (!Number.isInteger(codePoint) || codePoint < 0 || codePoint > 0x10ffff || isSurrogate) return entity;

    return String.fromCodePoint(codePoint);
  });

  const setPlainLines = (element, value) => {
    const fragment = document.createDocumentFragment();
    const lines = value.split('\n');

    for (let index = 0; index < lines.length; index += 1) {
      if (index > 0) fragment.appendChild(document.createElement('br'));

      fragment.appendChild(document.createTextNode(lines[index]));
    }

    element.replaceChildren(fragment);
  };

  const plainText = (value) => {
    if (typeof value !== 'string') return '';

    const stripped = value
      .replace(/<\s*br\s*\/?\s*>/gi, '\n')
      .replace(/<\s*\/\s*(?:p|div|li|h[1-6]|blockquote|tr|ul|ol)\s*>/gi, '\n')
      .replace(/<[^>]*>/g, '');

    return decodeText(stripped).replace(/[ \t]+\n/g, '\n').replace(/\n{3,}/g, '\n\n').trim();
  };

  const reviewCopy = (review) => ({
    title: plainText(review && review.title),
    body: plainText(review && review.body)
  });

  const hasCopy = (review) => {
    const { title, body } = reviewCopy(review);

    return Boolean(title || body);
  };

  class CustomerReviewsFeed extends HTMLElement {
    #observer = null;
    #abort = null;
    #started = false;

    connectedCallback() {
      if (this.#started) return;

      this.#started = true;

      if (!('IntersectionObserver' in window)) {
        this.#load();
        return;
      }

      this.#observer = new IntersectionObserver((entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;

        this.#observer.disconnect();
        this.#observer = null;
        this.#load();
      }, { rootMargin: ROOT_MARGIN });

      // The feed is empty until the cards exist, so it has no box and a
      // zero-area target can miss the intersection callback. The section
      // already has the heading and padding.
      this.#observer.observe(this.closest('.customer-reviews') || this);
    }

    disconnectedCallback() {
      if (this.#observer) {
        this.#observer.disconnect();
        this.#observer = null;
      }

      if (this.#abort) {
        this.#abort.abort();
        this.#abort = null;
      }

      this.#started = false;
    }

    #load() {
      const source = this.dataset.source || 'recent';
      const groupId = (this.dataset.groupId || '').trim();

      if (source === 'selected' && !groupId) {
        this.#showStatus(this.dataset.missingGroup);
        return;
      }

      const subscriberId = (this.dataset.subscriberId || '').trim();

      if (!subscriberId) {
        this.#showStatus(this.dataset.unavailable);
        return;
      }

      const limit = Math.min(12, Math.max(1, parseInt(this.dataset.limit, 10) || 8));
      const minimum = minimumRating(this.dataset.minRating);
      const pageSize = minimum ? FILTERED_PAGE_SIZE : limit;
      let query = 'limit=' + pageSize + '&orderBy=date%20desc';

      if (source === 'selected') query += '&groupId=' + encodeURIComponent(groupId);

      const url = API_ORIGIN + '/stores/' + encodeURIComponent(subscriberId) + '/reviews?' + query;

      this.#abort = new AbortController();
      this.#fetchReviews(url, this.#abort.signal, limit, minimum, subscriberId);
    }

    async #fetchReviews(url, signal, limit, minimum, subscriberId) {
      const matches = [];
      let next = url;
      let pages = 0;
      let failed = false;

      try {
        while (next && matches.length < limit && pages < MAX_PAGES) {
          const payload = await this.#request(next, signal);

          if (!payload) {
            failed = matches.length === 0;
            break;
          }

          const reviews = Array.isArray(payload.reviews) ? payload.reviews : [];

          for (const review of reviews) {
            if (!meetsRating(review, minimum) || !hasCopy(review)) continue;

            matches.push(review);
            if (matches.length >= limit) break;
          }

          pages += 1;
          next = matches.length < limit ? nextReviewsUrl(payload.nextUrl, subscriberId) : '';
        }

        if (signal.aborted) return;

        if (failed) {
          this.#showStatus(this.dataset.unavailable);
          return;
        }

        if (!this.#render(matches)) this.#showStatus(this.dataset.empty);
      } catch (error) {
        if (signal.aborted || (error && error.name === 'AbortError')) return;

        if (matches.length && this.#render(matches)) return;

        this.#showStatus(this.dataset.unavailable);
      }
    }

    async #request(url, signal) {
      // No custom headers. Okendo's storefront CORS allow-list rejects
      // okendo-api-version, and a headerless GET is what the browser can send.
      const response = await fetch(url, {
        credentials: 'omit',
        signal
      });

      if (signal.aborted) throw new DOMException('The operation was aborted.', 'AbortError');
      if (!response.ok) return null;

      const payload = await response.json();

      if (signal.aborted) throw new DOMException('The operation was aborted.', 'AbortError');

      return payload;
    }

    #render(reviews) {
      const template = this.querySelector('[data-customer-reviews-card]');
      const list = this.querySelector('[data-customer-reviews-list]');

      if (!template || !list || !template.content.firstElementChild) return false;

      const fragment = document.createDocumentFragment();
      let count = 0;

      for (const review of reviews) {
        if (!review || typeof review !== 'object') continue;

        const card = this.#card(template, review);

        if (!card) continue;

        fragment.appendChild(card);
        count += 1;
      }

      if (!count) return false;

      list.replaceChildren(fragment);
      this.#hideStatus();
      this.#mount();
      return true;
    }

    #card(template, review) {
      const { title, body } = reviewCopy(review);

      if (!title && !body) return null;

      const card = template.content.firstElementChild.cloneNode(true);
      const formatted = formatRating(review.rating);
      const stars = card.querySelector('.customer-reviews__stars');
      const score = card.querySelector('.customer-reviews__score');
      const ratingText = card.querySelector('.customer-reviews__rating-text');
      const titleEl = card.querySelector('.customer-reviews__title');
      const bodyEl = card.querySelector('.customer-reviews__body');

      if (stars) {
        const fill = formatted ? (Number(formatted) / 5) * 100 : 0;
        stars.style.setProperty('--customer-reviews-fill', fill + '%');
      }

      if (score) score.textContent = formatted;

      if (ratingText) {
        const label = this.dataset.ratingLabel || '';
        ratingText.textContent = label.replace('__RATING__', formatted);
      }

      if (titleEl) {
        if (title) {
          titleEl.textContent = '\u2018' + title + '\u2019';
        } else {
          titleEl.remove();
        }
      }

      if (bodyEl) {
        if (body) {
          setPlainLines(bodyEl, body);
        } else {
          bodyEl.remove();
        }
      }

      return card;
    }

    #showStatus(message) {
      const status = this.querySelector('[data-customer-reviews-status]');

      if (!status) return;

      if (!message) {
        status.hidden = true;
        status.textContent = '';
        return;
      }

      status.hidden = false;
      status.textContent = message;
    }

    #hideStatus() {
      const status = this.querySelector('[data-customer-reviews-status]');

      if (!status) return;

      status.hidden = true;
      status.textContent = '';
    }

    #mount() {
      const carousel = this.querySelector('[data-carousel]');

      if (!carousel) return;

      const mount = () => {
        if (window.themeCarousel) window.themeCarousel.evaluate(carousel);
      };

      if (window.themeCarousel) {
        mount();
        return;
      }

      let tries = 0;
      const timer = window.setInterval(() => {
        if (window.themeCarousel || tries++ > 60) {
          window.clearInterval(timer);
          mount();
        }
      }, 50);
    }
  }

  customElements.define(TAG, CustomerReviewsFeed);
})();
